import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { X, Copy, Printer } from "lucide-react";

const CATEGORIES = [
  { id: "artiste-non-cadre", label: "Artiste non-cadre", coeff: 2.0 },
  { id: "cadre", label: "Artiste et technicien cadre", coeff: 2.1 },
  { id: "technicien-non-cadre", label: "Technicien non-cadre", coeff: 1.95 },
];

const DISCLAIMER =
  "Il s'agit d'une estimation basée sur des salaires bruts médians. Données non contractuelles — pour toute demande particulière, nous consulter.";

const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);
const coeffLabel = (c) => c.toFixed(2).replace(".", ",");

export function CostEstimator({ onClose }) {
  const [brut, setBrut] = useState("");
  const [days, setDays] = useState("");
  const [catId, setCatId] = useState(CATEGORIES[0].id);

  const brutNum = parseFloat(String(brut).replace(/\s/g, "").replace(",", "."));
  const valid = Number.isFinite(brutNum) && brutNum > 0;
  const cat = CATEGORIES.find((c) => c.id === catId);
  const net = valid ? brutNum * 0.77 : 0;
  const total = valid ? net * cat.coeff : 0;
  const daysNum = parseInt(days, 10);
  const daysInfo =
    Number.isFinite(daysNum) && daysNum > 0 ? ` (sur ${daysNum} jour${daysNum > 1 ? "s" : ""})` : "";

  const summaryLines = () => [
    "ESTIMATION DE COÛT DE PAIE",
    `Brut : ${eur(brutNum)}${daysInfo}`,
    `Catégorie : ${cat.label} (coefficient ${coeffLabel(cat.coeff)})`,
    `Net estimé (Brut − 23 %) : ${eur(net)}`,
    `Coût total estimé (Net × ${coeffLabel(cat.coeff)}) : ${eur(total)}`,
    "",
    DISCLAIMER,
  ];

  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(summaryLines().join("\n"));
      toast.success("Estimation copiée dans le presse-papiers.");
    } catch {
      toast.error("Copie impossible dans le presse-papiers.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-[#0B2A52] border border-white/10 rounded-xl p-8 max-h-[90vh] overflow-y-auto"
        data-testid="estimator-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl">Estimateur de coût de paie</h2>
          <button onClick={onClose} data-testid="estimator-close-btn" aria-label="Fermer">
            <X className="w-5 h-5 text-[#A0B2CE]" />
          </button>
        </div>

        <div className="space-y-5">
          <div>
            <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">
              Montant brut global (€)
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={brut}
              onChange={(e) => setBrut(e.target.value)}
              placeholder="Ex : 3000"
              data-testid="estimator-brut-input"
              className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2.5 text-lg transition-colors placeholder:text-white/25"
            />
          </div>

          <div>
            <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">
              Nombre de jours (informatif)
            </label>
            <input
              type="number"
              min="0"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="Facultatif"
              data-testid="estimator-days-input"
              className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2.5 transition-colors placeholder:text-white/25"
            />
          </div>

          <div>
            <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE] mb-2">Catégorie</p>
            <div className="space-y-2">
              {CATEGORIES.map((c) => (
                <label
                  key={c.id}
                  className={`flex items-center justify-between gap-3 px-4 py-3 rounded-md border cursor-pointer transition-colors ${
                    catId === c.id ? "border-gold bg-gold/10" : "border-white/15 hover:border-white/30"
                  }`}
                >
                  <span className="flex items-center gap-3 text-sm">
                    <input
                      type="radio"
                      name="estimator-category"
                      checked={catId === c.id}
                      onChange={() => setCatId(c.id)}
                      data-testid={`estimator-cat-${c.id}`}
                      className="accent-[#C9A227]"
                    />
                    {c.label}
                  </span>
                  <span className="font-mono text-xs text-gold">× {coeffLabel(c.coeff)}</span>
                </label>
              ))}
            </div>
          </div>

          {valid && (
            <div className="border border-gold/30 rounded-md p-5 space-y-3" data-testid="estimator-result">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#A0B2CE]">Brut{daysInfo}</span>
                <span className="font-mono" data-testid="estimator-brut-value">{eur(brutNum)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#A0B2CE]">Net estimé (Brut − 23 %)</span>
                <span className="font-mono" data-testid="estimator-net-value">{eur(net)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-gold/30 pt-3">
                <span className="text-gold">Coût total estimé (Net × {coeffLabel(cat.coeff)})</span>
                <span className="font-mono text-lg text-gold" data-testid="estimator-total-value">{eur(total)}</span>
              </div>
            </div>
          )}

          {valid && (
            <div className="flex gap-3">
              <button
                onClick={copyResult}
                data-testid="estimator-copy-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors"
              >
                <Copy className="w-4 h-4" /> Copier
              </button>
              <button
                onClick={() => window.print()}
                data-testid="estimator-print-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors"
              >
                <Printer className="w-4 h-4" /> Imprimer
              </button>
            </div>
          )}

          <p className="text-[11px] text-[#A0B2CE] leading-relaxed italic" data-testid="estimator-disclaimer">
            {DISCLAIMER}
          </p>
        </div>
      </motion.div>

      {/* Version imprimable (visible uniquement à l'impression) */}
      {valid && (
        <div className="estimator-print" aria-hidden="true">
          {summaryLines().join("\n")}
        </div>
      )}
    </div>
  );
}
